import './projectMoneyRows.css';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { InvoiceDraftResponse } from '../../shared/invoicing';
import { QUOTE_STATUS_LABELS, type InvoiceRow, type ProjectRow } from '../../shared/types';
import { displayCents, type MoneyMode } from '../lib/money';
import { projectMoneyRows, type ProjectMoneyRow } from '../lib/projectMoneyRows';
import { formatDayMonth, formatInvoiceNumber, formatMoneyExact, Pill } from './primitives';

export function ProjectMoneyStatus({ status }: { status: ProjectRow['quote_status'] }) {
  return status ? <span className="money-status"><Pill tone={status === 'remainder_paid' ? 'ok' : status === 'deposit_paid' ? 'gold' : undefined}>
    {QUOTE_STATUS_LABELS[status]}</Pill></span> : null;
}
function stateText(row: ProjectMoneyRow): string {
  switch (row.state) {
    case 'not_drafted': return 'not drafted';
    case 'draft': return 'drafted';
    case 'paid': return `paid${row.date ? ` ${formatDayMonth(row.date)}` : ''}`;
    case 'overdue': return row.days_overdue ? `${row.days_overdue}d overdue` : 'overdue';
    case 'sent': return row.date ? `due ${formatDayMonth(row.date)}` : 'sent';
    case 'void': return 'void';
  }
}
const tone = (row: ProjectMoneyRow) => row.state === 'paid' ? 'ok' : row.state === 'overdue' ? 'danger'
  : row.state === 'sent' ? 'sent' : row.state === 'not_drafted' || row.state === 'draft' || row.state === 'void' ? 'none' : '';

/** Shared Money-group body for the rail and phone accordion. Both keep the
 * quote editor provided by their surface and the existing invoice action. */
export function ProjectMoneyRows({ draft, invoices, today, mode, quote, quoteStatus, action, crew, mobile = false }: {
  draft?: InvoiceDraftResponse | null;
  invoices: InvoiceRow[];
  today: string;
  mode: MoneyMode;
  quote: ReactNode;
  quoteStatus: ProjectRow['quote_status'];
  action?: ReactNode;
  crew?: ReactNode;
  mobile?: boolean;
}) {
  const free = draft?.strip.free || quoteStatus === 'free_project';
  const rows = projectMoneyRows(draft, invoices, today);
  const actual = draft?.invoiced.existing ?? invoices;
  const count = actual.filter((invoice) => invoice.status !== 'void').length;
  const strip = draft?.strip;
  return <div className={`money-rows${mobile ? ' mobile' : ''}`}>
    {free ? <div className="mr-free">Free project, nothing to invoice</div> : <>
      <div className="mr-quote" data-rail-control="quote"><span className="k">Quote{mode === 'incl' && <small>ex GST</small>}</span><span className="v">{quote}</span><span className="sub">{count} {count === 1 ? 'invoice' : 'invoices'}</span></div>
      <div data-rail-control="invoices">{rows.map((row, index) => {
        const contents = <><span className="k">{row.label}</span><span className={`mr-st ${tone(row)}`}>
          {row.number && <span className="num">{formatInvoiceNumber(row.number)}</span>}{stateText(row)}
        </span><span className="amt">{formatMoneyExact(displayCents(row.amount_cents, row.gst_cents, mode))}</span></>;
        const cls = `mr-row${row.state === 'not_drafted' || row.state === 'void' ? ' none' : ''}`;
        return row.id ? <Link className={cls} key={row.id} to={`/finance/invoices/${row.id}`} title={`${row.label} ${row.number ?? ''} · ${stateText(row)}`}>{contents}</Link>
          : <div className={cls} key={`pending-${index}`}>{contents}</div>;
      })}</div>
      {strip?.mode === 'generic' && <div className="mr-summary">
        <span className="ok">{formatMoneyExact(strip.paid_ex_cents)} paid</span>{' · '}
        <span className={strip.overdue ? 'danger' : undefined}>{formatMoneyExact(strip.outstanding_ex_cents)} outstanding</span>{' · ex GST'}
        {strip.overdue && <span className="danger"> · {strip.days_overdue}d overdue</span>}
        <Link className="mr-hint" to={`/finance/invoices/${strip.untagged_invoice_id}`}>Set invoice kinds to see deposit and remainder</Link>
      </div>}
      {action && <div className="mr-action" data-rail-control="invoice-action">{action}</div>}
    </>}
    {crew}
  </div>;
}
